
import React, { useState, useEffect } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import { FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { GraduationCap, Search } from 'lucide-react';
import { RegistrationFormValues } from '@/schemas/registrationSchema';
import { classYears } from '@/data/classYears';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { motion } from 'framer-motion';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface ClassSelectorProps {
  control: Control<RegistrationFormValues>;
  onYearChange: (value: string | null) => void;
}

const ClassSelector: React.FC<ClassSelectorProps> = ({ control, onYearChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredClasses, setFilteredClasses] = useState(classYears);
  
  // Watch the class field to update the selected option when returning from QR screen
  const classValue = useWatch({
    control,
    name: "class"
  });
  
  // Get the currently selected option based on the form value
  const selectedClass = classYears.find(option => option.value === classValue);
  
  // Update the year display whenever the class value changes
  useEffect(() => {
    onYearChange(classValue || null);
  }, [classValue, onYearChange]);
  
  // Filter classes when search term changes
  useEffect(() => {
    const filtered = classYears.filter(cls => 
      cls.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cls.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredClasses(filtered);
  }, [searchTerm]);
  
  // Organize classes into grid
  const splitIntoGroups = (items: typeof classYears) => {
    const result = [];
    for (let i = 0; i < items.length; i += 3) {
      result.push(items.slice(i, i + 3));
    }
    return result;
  };
  
  const classGroups = splitIntoGroups(filteredClasses);

  return (
    <div className="space-y-4">
      <FormItem>
        <FormLabel className="flex items-center gap-2">
          <GraduationCap size={16} className="text-blue-500" />
          Lớp <span className="text-red-500 font-bold">*</span>
        </FormLabel>
        <FormControl>
          <Controller
            control={control}
            name="class"
            render={({ field, fieldState }) => (
              <>
                <div 
                  className={`relative w-full border rounded-md p-3 cursor-pointer transition-all duration-300 
                    ${fieldState.error 
                      ? 'border-red-500' 
                      : field.value 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-blue-300'}`}
                  onClick={() => setIsOpen(true)}
                  role="button"
                  tabIndex={0}
                  aria-haspopup="dialog"
                  aria-expanded={isOpen}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setIsOpen(true);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GraduationCap size={18} className="text-blue-500" />
                      <span className={field.value ? 'font-medium text-blue-800' : 'text-gray-400'}>
                        {selectedClass?.label || 'Nhấn để chọn lớp'}
                      </span>
                    </div>
                    <Search size={18} className="text-gray-400" />
                  </div>
                </div>
                
                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                  <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto bg-gradient-to-b from-white to-blue-50 border border-blue-100 shadow-lg backdrop-blur-sm">
                    <DialogTitle className="text-center text-blue-800 font-semibold tracking-wide border-b border-blue-100 pb-3 mb-3">Chọn lớp của bạn</DialogTitle>
                    <div className="space-y-5 p-1">
                      <div className="relative">
                        <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400" />
                        <Input
                          type="text"
                          placeholder="Tìm kiếm lớp..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 border-blue-200 focus:border-blue-400 focus:ring-blue-300 transition-all rounded-full"
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            aria-label="Xóa tìm kiếm"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                      
                      {filteredClasses.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 italic bg-blue-50 rounded-lg">
                          Không tìm thấy lớp nào phù hợp
                        </div>
                      ) : (
                        <div className="space-y-5">
                          {classGroups.map((group, groupIndex) => (
                            <div key={groupIndex} className="grid grid-cols-3 gap-3">
                              {group.map((cls) => (
                                <motion.button
                                  key={cls.value}
                                  type="button"
                                  whileHover={{ scale: 1.03, boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)" }}
                                  whileTap={{ scale: 0.98 }}
                                  transition={{ duration: 0.2 }}
                                  onClick={() => {
                                    field.onChange(cls.value);
                                    setIsOpen(false);
                                    onYearChange(cls.value);
                                  }}
                                  className={`flex flex-col items-center p-3 rounded-lg transition-all duration-300 ${
                                    cls.value === field.value
                                      ? 'border-2 border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm'
                                      : 'border border-gray-100 hover:border-blue-200 hover:bg-blue-50'
                                  }`}
                                  aria-selected={cls.value === field.value}
                                >
                                  <span className="text-sm font-medium mb-1 text-blue-700">{cls.label}</span>
                                  {cls.image && (
                                    <div className="w-full h-16 overflow-hidden rounded-md shadow-sm transition-all hover:shadow">
                                      <img
                                        src={cls.image}
                                        alt={`Lớp ${cls.label}`}
                                        className="w-full h-full object-cover transition-all duration-300 hover:scale-105"
                                        loading="lazy"
                                      />
                                    </div>
                                  )}
                                </motion.button>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                
                {!field.value && (
                  <div className="text-amber-500 text-xs md:text-sm mt-1 animate-in fade-in-50 duration-200">
                    Vui lòng chọn lớp của bạn
                  </div>
                )}
              </>
            )}
          />
        </FormControl>
        <FormMessage className="text-red-500 animate-in fade-in-50 duration-200" />
      </FormItem>
    </div>
  );
};

export default ClassSelector;
